import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Alert,
  Typography,
  Box,
  Divider,
  CircularProgress
} from '@mui/material';
import { Share, PersonAdd, ContentCopy, Check, Close, Delete, Email } from '@mui/icons-material';
import { addAdviseShare, removeAdviseShare } from '../../../Api/Advise/Engagements';
import { AdviseEngagement } from '../../../types/advise';
import { isValidEmail } from '../../../utils/email';

interface ShareEngagementDialogProps {
  open: boolean;
  onClose: () => void;
  engagement: AdviseEngagement;
  onShared: (engagement: AdviseEngagement) => void;
}

type CopiedKey = string;

export const ShareEngagementDialog = ({ open, onClose, engagement, onShared }: ShareEngagementDialogProps) => {
  const [email, setEmail] = useState('');
  const [isSharing, setIsSharing] = useState(false);
  const [error, setError] = useState('');
  const [emailNotice, setEmailNotice] = useState('');
  const [copiedStates, setCopiedStates] = useState<Record<CopiedKey, boolean>>({});

  // The API creates the link's token on the first share, so there is no link to copy or send before then.
  const hasLink = !!engagement.share_token;

  const buildInviteLink = useCallback(() => {
    if (!engagement.share_token) return '';
    return `${window.location.origin}/advise/shared?id=${engagement.id}&token=${engagement.share_token}`;
  }, [engagement.id, engagement.share_token]);

  const handleCopy = useCallback(async (text: string, key: CopiedKey) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStates((prev) => ({ ...prev, [key]: true }));
      setTimeout(() => {
        setCopiedStates((prev) => ({
          ...prev,
          [key]: false
        }));
      }, 2000);
    } catch {
      setError('Failed to copy to clipboard');
    }
  }, []);

  const buildEmailTemplate = useCallback(
    (targetEmail: string) => {
      const link = buildInviteLink();
      return [
        `Hi ${targetEmail},`,
        '',
        "You've been invited to view an engagement:",
        `"${engagement.engagement_name}"`,
        '',
        'Click the link below to view it:',
        link
      ].join('\n');
    },
    [engagement.engagement_name, buildInviteLink]
  );

  const handleMailto = useCallback(
    (targetEmail: string) => {
      const subject = encodeURIComponent(`Shared Engagement: ${engagement.engagement_name}`);
      const body = encodeURIComponent(buildEmailTemplate(targetEmail));
      window.open(`mailto:${targetEmail}?subject=${subject}&body=${body}`);
    },
    [engagement.engagement_name, buildEmailTemplate]
  );

  const handleShare = useCallback(async () => {
    setError('');
    setEmailNotice('');

    const emailLc = email.trim().toLowerCase();
    if (!emailLc) {
      setError('Please enter an email address.');
      return;
    }
    if (!isValidEmail(emailLc)) {
      setError('Please enter a valid email address.');
      return;
    }

    const isDuplicate = engagement.shared_with?.some((e) => e.toLowerCase() === emailLc);
    if (isDuplicate) {
      setError('This email already has access.');
      return;
    }

    setIsSharing(true);
    const result = await addAdviseShare(engagement.id, [emailLc]);
    setIsSharing(false);

    if (result.error) {
      setError(result.error.message);
      return;
    }

    if (result.data) onShared(result.data);
    setEmail('');
    setEmailNotice(`Shared with ${emailLc}. Send them the invite link.`);
  }, [email, engagement, onShared]);

  const handleRemove = useCallback(
    async (targetEmail: string) => {
      setError('');
      const result = await removeAdviseShare(engagement.id, targetEmail);
      if (result.error) {
        setError(result.error.message);
        return;
      }
      if (result.data) onShared(result.data);
    },
    [engagement.id, onShared]
  );

  const handleClose = useCallback(() => {
    setError('');
    setEmail('');
    setEmailNotice('');
    setCopiedStates({});
    onClose();
  }, [onClose]);

  const sharedWith = engagement.shared_with ?? [];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
        <Share fontSize="small" />
        Share Engagement
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {emailNotice && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setEmailNotice('')}>
            {emailNotice}
          </Alert>
        )}

        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ mb: 2 }}>
          Share &quot;{engagement.engagement_name}&quot; with collaborators. They&apos;ll receive a link to view the
          engagement.
        </Typography>

        {/* Add collaborator */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            size="small"
            fullWidth
            label="Email address"
            placeholder="colleague@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleShare();
            }}
            disabled={isSharing}
          />
          <Button
            variant="contained"
            onClick={handleShare}
            disabled={isSharing || !email.trim()}
            startIcon={isSharing ? <CircularProgress size={16} /> : <PersonAdd />}
            sx={{ whiteSpace: 'nowrap' }}>
            Share
          </Button>
        </Box>

        {/* Quick actions for entered email */}
        {isValidEmail(email) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Button
              size="small"
              variant="outlined"
              disabled={!hasLink}
              startIcon={copiedStates['link'] ? <Check /> : <ContentCopy />}
              onClick={() => handleCopy(buildInviteLink(), 'link')}>
              {copiedStates['link'] ? 'Copied!' : 'Copy Link'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={!hasLink}
              startIcon={copiedStates['template'] ? <Check /> : <ContentCopy />}
              onClick={() => handleCopy(buildEmailTemplate(email.trim().toLowerCase()), 'template')}>
              {copiedStates['template'] ? 'Copied!' : 'Copy Email'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={!hasLink}
              startIcon={<Email />}
              onClick={() => handleMailto(email.trim().toLowerCase())}>
              Mailto
            </Button>
            {!hasLink && (
              <Typography
                variant="caption"
                color="textSecondary">
                Share first to create the link.
              </Typography>
            )}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Existing collaborators */}
        <Typography
          variant="subtitle2"
          sx={{ mb: 1 }}>
          Shared with ({sharedWith.length})
        </Typography>

        {sharedWith.length === 0 ? (
          <Typography
            variant="body2"
            color="textSecondary">
            Not shared with anyone yet.
          </Typography>
        ) : (
          sharedWith.map((sharedEmail) => (
            <Box
              key={sharedEmail}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 0.5
              }}>
              <Typography variant="body2">{sharedEmail}</Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton
                  size="small"
                  title="Copy link"
                  onClick={() => handleCopy(buildInviteLink(), sharedEmail)}>
                  {copiedStates[sharedEmail] ? (
                    <Check
                      fontSize="small"
                      color="success"
                    />
                  ) : (
                    <ContentCopy fontSize="small" />
                  )}
                </IconButton>
                <IconButton
                  size="small"
                  title="Send email"
                  onClick={() => handleMailto(sharedEmail)}>
                  <Email fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  title="Remove access"
                  color="error"
                  onClick={() => handleRemove(sharedEmail)}>
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ))
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          startIcon={<Close />}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
